/**
 * Per-provider extra fields collected by the Add-API-key modal.
 *
 * The `apiKey` field is universal so it's owned by the modal itself.
 * Anything else (Atlassian needs siteUrl + email; GitLab takes an
 * optional self-hosted URL; Trello calls its second value `appKey`) is
 * declared here and posted under `config` so the backend can keep its
 * per-provider validation isolated.
 */
export interface ApiKeyFieldSchema {
  /** Key written into the POST body's `config` object. */
  name: string;
  /** Label shown above the input. */
  label: string;
  /** Helper text rendered as a small muted line below the field. */
  helpText?: string;
  type?: 'text' | 'email' | 'url' | 'password';
  placeholder?: string;
  required?: boolean;
}

export interface ApiKeyProviderSchema {
  /** Field collected as the credential's primary secret (`apiKey` in the body). */
  apiKey: {
    label: string;
    placeholder?: string;
    helpText?: string;
  };
  /** Extra fields posted under `config`. */
  configFields: ApiKeyFieldSchema[];
}

const ATLASSIAN: ApiKeyProviderSchema = {
  apiKey: {
    label: 'API token',
    placeholder: 'ATATT3xFfGF…',
    helpText:
      'Generate at id.atlassian.com → Security → API tokens. Paired with the email below to call the Atlassian Cloud APIs.',
  },
  configFields: [
    {
      name: 'email',
      label: 'Atlassian account email',
      type: 'email',
      placeholder: 'you@example.com',
      required: true,
    },
    {
      name: 'siteUrl',
      label: 'Site URL',
      type: 'url',
      placeholder: 'https://your-org.atlassian.net',
      helpText:
        'Used to validate the token and resolve the underlying cloudId.',
      required: true,
    },
  ],
};

const GITLAB: ApiKeyProviderSchema = {
  apiKey: {
    label: 'Personal access token',
    placeholder: 'glpat-…',
    helpText: 'Create at gitlab.com → Preferences → Access tokens.',
  },
  configFields: [
    {
      name: 'gitlabUrl',
      label: 'GitLab URL (optional)',
      type: 'url',
      placeholder: 'https://gitlab.com',
      helpText: 'Leave blank for gitlab.com. Set for self-hosted instances.',
    },
  ],
};

const DATADOG: ApiKeyProviderSchema = {
  // The catalog's `apiKeyHeaders` drives the secret inputs for Datadog; this
  // single-key label is an unused fallback for catalogs that haven't migrated.
  apiKey: { label: 'API key' },
  configFields: [
    {
      name: 'site',
      label: 'Datadog site',
      type: 'text',
      placeholder: 'datadoghq.com',
      helpText:
        'Use datadoghq.eu for EU accounts, datadoghq.com for US. Leave blank to accept the backend default.',
    },
  ],
};

const TRELLO: ApiKeyProviderSchema = {
  apiKey: {
    label: 'User token',
    placeholder: 'ATTA…',
    helpText:
      'Generated against the app key below. Trello scopes auth via two values, so both are required.',
  },
  configFields: [
    {
      name: 'appKey',
      label: 'App key',
      type: 'text',
      placeholder: '0123456789abcdef…',
      helpText:
        'Obtain at trello.com/app-key. The app key is the public identifier paired with the user token above.',
      required: true,
    },
  ],
};

const SLACK: ApiKeyProviderSchema = {
  // Slack's bot token is a single value; the required bot scopes are rendered
  // separately from the catalog's `apiKeySetupInstructions`.
  apiKey: {
    label: 'Bot User OAuth Token',
    placeholder: 'xoxb-…',
    helpText:
      'From your Slack app under OAuth & Permissions → Bot User OAuth Token. One token per workspace.',
  },
  configFields: [],
};

/** Returns the schema for a provider, or a sensible default (key-only). */
export function apiKeySchemaFor(provider: string): ApiKeyProviderSchema {
  switch (provider) {
    case 'atlassian':
      return ATLASSIAN;
    case 'datadog':
      return DATADOG;
    case 'gitlab':
      return GITLAB;
    case 'slack':
      return SLACK;
    case 'trello':
      return TRELLO;
    default:
      return {
        apiKey: { label: 'API key' },
        configFields: [],
      };
  }
}
